import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { useAuth } from '../components/auth/AuthContext';
import './AgentChatDashboard.css';

const PORTAL_SERVICES_URL = '/server/portal_services';

function AgentChatDashboard() {
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [showMobileList, setShowMobileList] = useState(true);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Fetch všech konverzací
  const fetchConversations = useCallback(async () => {
    try {
      const response = await fetch(`${PORTAL_SERVICES_URL}/chat/conversations`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
        setTotalUnread(data.totalUnread || 0);
        
        // Pokud máme vybraný chat, aktualizuj jeho data
        if (selectedChat) {
          const updated = data.conversations?.find(
            c => c.module === selectedChat.module && c.recordId === selectedChat.recordId
          );
          if (updated) {
            setSelectedChat(updated);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedChat]);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 15000); // Refresh každých 15s
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // Scroll na konec zpráv
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedChat?.messages]);

  const handleSelectChat = async (chat) => {
    setSelectedChat(chat);
    setShowMobileList(false);
    
    // Označit zprávy jako přečtené
    if (chat.unreadCount > 0) {
      try {
        await fetch(`${PORTAL_SERVICES_URL}/chat/mark-read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ module: chat.module, recordId: chat.recordId })
        });
        // Refresh po označení
        setTimeout(fetchConversations, 500);
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedChat) return;

    setSendingReply(true);
    try {
      const response = await fetch(`${PORTAL_SERVICES_URL}/chat/agent-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          module: selectedChat.module,
          recordId: selectedChat.recordId,
          message: replyText.trim(),
          agentName: currentUser?.first_name || 'Agent'
        })
      });

      if (response.ok) {
        setReplyText('');
        toast.success('Odpověď odeslána');
        fetchConversations();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Chyba při odesílání');
      }
    } catch (error) {
      toast.error('Chyba při odesílání');
    } finally {
      setSendingReply(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit' });
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('cs-CZ', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  const openRecord = () => {
    if (!selectedChat) return;
    if (selectedChat.module === 'Quotes') {
      navigate(`/quote/${selectedChat.recordId}`);
    } else {
      window.open(`https://crm.zoho.eu/crm/org20068895485/tab/${selectedChat.module}/${selectedChat.recordId}`, '_blank');
    }
  };

  return (
    <div className="agent-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <button 
            className="btn-back mobile-only"
            onClick={() => setShowMobileList(true)}
            style={{ display: showMobileList ? 'none' : 'flex' }}
          >
            ←
          </button>
          <h1>💬 Chat Dashboard</h1>
          {totalUnread > 0 && (
            <span className="unread-badge header-badge">{totalUnread}</span>
          )}
        </div>
        <div className="header-right">
          <button onClick={() => navigate('/')} className="btn-nav">
            📄 Quote Editor
          </button>
          <span className="user-name">{currentUser?.first_name}</span>
        </div>
      </header>

      <div className="dashboard-content">
        {/* Conversation List */}
        <aside className={`conversation-list ${showMobileList ? 'show' : ''}`}>
          <div className="list-header">
            <h2>Konverzace</h2>
            <button onClick={fetchConversations} className="btn-refresh" title="Obnovit">
              🔄
            </button>
          </div>

          {loading ? (
            <div className="loading-state">Načítám...</div>
          ) : conversations.length === 0 ? (
            <div className="empty-state">
              <p>Žádné konverzace</p>
            </div>
          ) : (
            <div className="conversations">
              {conversations.map((chat) => {
                const chatKey = `${chat.module}_${chat.recordId}`;
                const isSelected = selectedChat?.module === chat.module && 
                                   selectedChat?.recordId === chat.recordId;
                
                return (
                  <div
                    key={chatKey}
                    className={`conversation-item ${isSelected ? 'selected' : ''} ${chat.unreadCount > 0 ? 'has-unread' : ''}`}
                    onClick={() => handleSelectChat(chat)}
                  >
                    <div className="conv-icon">
                      {chat.module === 'Quotes' ? '📄' : '📦'}
                    </div>
                    <div className="conv-content">
                      <div className="conv-header">
                        <span className="conv-title">
                          ...{chat.recordId?.slice(-6)}
                        </span>
                        <span className="conv-time">{formatDate(chat.lastMessageAt)}</span>
                      </div>
                      <div className="conv-preview">
                        <span className={`preview-text ${chat.lastAuthorType === 'agent' ? 'from-agent' : ''}`}>
                          {chat.lastAuthorType === 'agent' && '↩ '}
                          {chat.lastMessagePreview || 'Žádné zprávy'}
                        </span>
                        {chat.unreadCount > 0 && (
                          <span className="unread-badge">{chat.unreadCount}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </aside>

        {/* Chat Detail */}
        <main className={`chat-detail ${!showMobileList ? 'show' : ''}`}>
          {selectedChat ? (
            <>
              <div className="chat-header">
                <div className="chat-info">
                  <span className="chat-icon">
                    {selectedChat.module === 'Quotes' ? '📄' : '📦'}
                  </span>
                  <div>
                    <h3>
                      {selectedChat.module === 'Quotes' ? 'Nabídka' : 'Objednávka'} 
                      {' '}...{selectedChat.recordId?.slice(-6)}
                    </h3>
                    <span className="chat-subtitle">
                      {selectedChat.messages?.length || 0} zpráv
                    </span>
                  </div>
                </div>
                <button onClick={openRecord} className="btn-open-record">
                  Otevřít záznam →
                </button>
              </div>

              <div className="messages-container">
                {selectedChat.messages?.slice().reverse().map((msg) => (
                  <div 
                    key={msg.ROWID} 
                    className={`message ${msg.Author_Type === 'agent' ? 'from-agent' : 'from-customer'}`}
                  >
                    <div className="message-bubble">
                      <div className="message-header">
                        <span className="message-author">{msg.Author_Name || (msg.Author_Type === 'agent' ? 'Agent' : 'Zákazník')}</span>
                        <span className="message-time">{formatFullDate(msg.Created_At)}</span>
                      </div>
                      <div className="message-text">{msg.Message}</div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="reply-container">
                <textarea
                  placeholder="Napište odpověď..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleReply();
                    }
                  }}
                  rows={2}
                />
                <button
                  onClick={handleReply}
                  disabled={sendingReply || !replyText.trim()}
                  className="btn-send"
                >
                  {sendingReply ? '...' : 'Odeslat'}
                </button>
              </div>
            </>
          ) : (
            <div className="no-chat-selected">
              <div className="empty-icon">💬</div>
              <p>Vyberte konverzaci ze seznamu</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default AgentChatDashboard;
